import React from "react";
import { Modal, Button, Avatar } from "antd";
import { ArrowLeftOutlined, CloseOutlined, ShareAltOutlined, MoreOutlined, RightOutlined } from "@ant-design/icons";
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import { useHistory } from "react-router-dom"; 
import dummy from '../../../../assets/Images/No-image-found.jpeg';
import "./PostPreview.css";

interface PostPreviewProps {
  visible: boolean;
  onCancel: () => void;
  onBack: () => void;
  onEdit: () => void;
  onPost: () => void;
  onShare: () => void;
  onUnpublish: () => void;
  postData: any;
  imagesData?: any[];
  videosData?: any[];
  documentsData?: any[];
  audiosData?: any[];
}

const PostPreview: React.FC<PostPreviewProps> = ({
  visible,
  onCancel,
  onBack,
  onEdit,
  onPost,
  onShare,
  onUnpublish,
  postData,
  imagesData = [],
  videosData = [],
  documentsData = [],
  audiosData = []
}) => {
  const history = useHistory();

  const handleEventClick = () => {
    if (postData.linkedEvent && postData.linkedEvent.entityId) {
      history.push(`/spaces/${postData.linkedEvent.entityId}`);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={600}
      className="post-preview-modal"
      closeIcon={<CloseOutlined />}
    >
      <div className="post-preview-container">
        {/* Header */}
        <div className="post-preview-header">
          <div className="header-left">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={onBack}
              className="back-button"
            />
            <h2 className="preview-title">Preview post</h2>
          </div>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={onCancel}
            className="close-button"
          />
        </div>

        {/* Content */}
        <div className="post-preview-content">
          <div className="post-card">
            {/* Author Info */}
            <div className="author-section">
              <div className="author-left">
                <Avatar src={postData.authorAvatar} size={40} className="author-avatar" />
                <div className="author-info">
                  <div className="author-name-row">
                    <span className="author-name">{postData.authorName}</span>
                  </div>
                </div>
              </div>
              <Button type="text" icon={<MoreOutlined />} className="more-button" />
            </div>

            {/* Post Title */}
            <h3 className="post-title">{postData.title}</h3>

            {/* Post Description */}
            <div
              className="post-description"
              dangerouslySetInnerHTML={{ __html: postData.description }}
            />

            {/* Linked Event Section */}
            {postData.linkedEvent && (
              <div
                className="linked-event-section"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  marginTop: '12px',
                  border: '1px solid #e9ecef',
                  cursor: 'pointer' 
                }}
                onClick={handleEventClick}
              >
                <Avatar
                  src={postData.linkedEvent.coverPhotos?.[0]?.post || postData.linkedEvent.avatar || dummy}
                  size={32}
                  style={{ marginRight: '12px' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}>
                    {postData.linkedEvent.name || 'Event'}
                  </div>
                </div>
                <RightOutlined style={{ color: '#9ca3af', fontSize: '14px' }} />
              </div>
            )}

            {/* Images Section */}
            {imagesData.length > 0 && (
              <div className="preview-images-section">
                <div className="preview-images-grid">
                  {imagesData.map((image, index) => (
                    <div
                      key={image.id}
                      className={index === 0 && imagesData.length > 1
                        ? "preview-image-item full-width-preview"
                        : "preview-image-item three-col-preview"}
                    >
                      <img
                        src={image.url || dummy}
                        alt={`Preview ${index + 1}`}
                        className="preview-uploaded-image"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Audio Section */}
            {audiosData.length > 0 && (
              <div className="preview-audio-section">
                {audiosData.map((audio, index) => (
                  <div key={audio.id || index} className="preview-audio-item">
                    <audio controls style={{ width: '100%' }}>
                      <source src={audio.url} type="audio/mpeg" />
                      Your browser does not support the audio element.
                    </audio>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className="action-buttons">
              <Button className="unpublish-button" type="text">
                <ArrowCounterClockwise size={16} style={{ marginRight: '10px' }} />
                Unpublish
              </Button>
              <Button className="share-button" onClick={onShare}>
                <ShareAltOutlined />
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="post-preview-footer">
          <Button className="edit-button" onClick={onEdit}>
            Edit
          </Button>
          <Button type="primary" className="post-button" onClick={onPost}>
            Post
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default PostPreview;